import { useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $isFreePlan } from '@nx-frontend/shared/store/org-info';
import { $isOwner } from '@nx-frontend/shared/store/org-role';
import { MenuItemType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { ChatbotContext } from './chatbot-context';
import { ChatbotPromptComponent } from './chatbot-prompt';
import { ChatbotThreadComponent } from './chatbot-thread';
import { $aiMainPagePrompts, $aiThreads } from './store';
import { UserLocation } from './types';

dayjs.extend(relativeTime);

interface ChatbotMainPageProps {
  onSendMessage: (value: string) => void;
  onViewHistoryClick: () => void;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onSelectThread: (id: string) => void;
  onThreadDelete: (id: string) => void;
  showLimitBanner: boolean;
  onCloseLimitBanner: () => void;
  onShowUpgradePopup: () => void;
  onShowAddonPopup: () => void;
  currentLocation: UserLocation;
}

export function ChatbotMainPage({
  onSendMessage,
  onViewHistoryClick,
  themeColors,
  themeStyle,
  onSelectThread,
  onThreadDelete,
  showLimitBanner,
  onCloseLimitBanner,
  onShowUpgradePopup,
  onShowAddonPopup,
  currentLocation,
}: ChatbotMainPageProps) {
  const aiContext = useContext(ChatbotContext);
  const [aiThreads, aiMainPagePrompts, isOrgOwner, isFreePlan] = useUnit([
    $aiThreads,
    $aiMainPagePrompts,
    $isOwner,
    $isFreePlan,
  ]);
  const t = useTranslations('features');

  const aiMainPagePromptsToUse = aiMainPagePrompts.slice();

  // add custom prompts for notes, but not for assistant in portals
  if (currentLocation.type === MenuItemType.NOTE && !aiContext.portalId) {
    // replace two first prompts with prompts specific for pages
    if (aiMainPagePromptsToUse.length >= 2) {
      aiMainPagePromptsToUse.splice(0, 2);
    }
    aiMainPagePromptsToUse.unshift({
      id: 'page-prompt-2',
      text: t('ai-helper.call_to_actions_for_page'),
    });
    aiMainPagePromptsToUse.unshift({
      id: 'page-prompt-1',
      text: t('ai-helper.summarize_this_page'),
    });
  }

  const relatedThreadsIds = Object.keys(aiThreads.threads)
    .filter((threadId) => aiThreads.threads[threadId].workspaceId === aiContext.workspaceId)
    .sort((a, b) => {
      return aiThreads.threads[a].updatedAt > aiThreads.threads[b].updatedAt ? -1 : 1;
    })
    .slice(0, 4);

  const showLimitNotification = useMemo(() => {
    if (isOrgOwner && aiThreads.usage) {
      const percentsLeft = 100 - (aiThreads.usage.current * 100) / aiThreads.usage.max;
      return percentsLeft <= 10;
    }
    return false;
  }, [aiThreads.usage, isOrgOwner]);

  const onCloseLimitBannerClick = useCallback(() => {
    onCloseLimitBanner();
  }, [onCloseLimitBanner]);

  const onUpgradeClick = useCallback(() => {
    onShowUpgradePopup();
  }, [onShowUpgradePopup]);

  const onAddonClick = useCallback(() => {
    onShowAddonPopup();
  }, [onShowAddonPopup]);

  return (
    <div className="overflow-y-auto grow p-8 overflow-x-hidden">
      <div className="flex items-center justify-center flex-col min-h-full w-full">
        <span className="text-xl font-semibold mb-2">{t('ai-helper.ai_greeting')}</span>
        <span className="whitespace-break-spaces text-center text-sm text-neutral-500 w-[320px]">
          {t('ai-helper.select_suggested_prompt')}
        </span>
        {aiMainPagePromptsToUse.length || relatedThreadsIds.length ? (
          <div className="flex justify-between mt-12 w-full">
            {aiMainPagePromptsToUse.length ? (
              <div
                className={classNames('flex flex-col gap-2', {
                  'w-full': !relatedThreadsIds.length,
                  'w-1/2 basis-1/2 pr-2': relatedThreadsIds.length,
                })}
              >
                <h2 className="font-semibold text-sm mb-3 mt-0">{t('ai-helper.related_for_you')}</h2>
                {aiMainPagePromptsToUse.map(({ text }, index) => (
                  <ChatbotPromptComponent
                    key={'prompt-main-' + index}
                    text={text}
                    onClick={onSendMessage}
                    themeColors={themeColors}
                    themeStyle={themeStyle}
                    bordered={true}
                    promptType="promptSuggestion"
                  />
                ))}
              </div>
            ) : null}
            {relatedThreadsIds.length ? (
              <div
                className={classNames('flex flex-col gap-2', {
                  'w-full': !aiMainPagePromptsToUse.length,
                  'w-1/2 basis-1/2 pl-2': aiMainPagePromptsToUse.length,
                })}
              >
                <div className="flex justify-between items-bottom mb-3">
                  <h2 className="font-semibold text-sm m-0">{t('ai-helper.latest_chat')}</h2>
                  <button
                    className="cursor-pointer text-xs text-neutral-500 hover:text-neutral-800 transition-colors duration-200"
                    onClick={onViewHistoryClick}
                  >
                    {t('ai-helper.view_more')}
                  </button>
                </div>
                {relatedThreadsIds.map((threadId) => (
                  <ChatbotThreadComponent
                    key={threadId}
                    id={threadId}
                    title={aiThreads.threads[threadId].title}
                    date={aiThreads.threads[threadId].updatedAt}
                    onClick={onSelectThread}
                    onDelete={onThreadDelete}
                    themeColors={themeColors}
                    themeStyle={themeStyle}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {showLimitNotification && showLimitBanner ? (
          !isFreePlan ? (
            <div className="chatbot-limit-banner mt-10 w-full p-2.5 rouded-lg flex items-center justify-between">
              <span>
                {t.rich('ai-helper.nearing_limits_add_on', {
                  button: (chunk) => (
                    <button className="text-cyan-900 underline font-bold text-sm" onClick={onAddonClick}>
                      {chunk}
                    </button>
                  ),
                })}
              </span>
              <button className="p-1 cursor-pointer" onClick={onCloseLimitBannerClick}>
                <Icon icon={IconsMap.CLOSE} className="w-4" />
              </button>
            </div>
          ) : (
            <div className="chatbot-limit-banner mt-10 w-full p-2.5 rouded-lg flex items-center justify-between">
              <span>
                {t.rich('ai-helper.nearing_limits_upgrade', {
                  button: (chunk) => (
                    <button className="text-cyan-900 underline font-bold text-sm" onClick={onUpgradeClick}>
                      {chunk}
                    </button>
                  ),
                })}
              </span>
              <button className="p-1 cursor-pointer" onClick={onCloseLimitBannerClick}>
                <Icon icon={IconsMap.CLOSE} className="w-4" />
              </button>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
